import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CustomToast from "src/components/CustomToast/CustomToast";
import cmsClient from "src/services/cmsClient";
import { adaptUserReport } from "src/features/reporting/adapters";
import IndividualObservationTableCell from "../../components/IndividualObservationTableCell/IndividualObservationTableCell";
import GreatConversationTableCell from "../../components/GreatConversationTableCell/GreatConversationTableCell";

const columnHelper = createColumnHelper<any>();

const requestUserReportExport = async (
	year: number,
	form: number,
	type: "complete" | "compliant",
	store?: number,
	area?: number
) => {
	await cmsClient.post("/observations/reports/user-by-month/", {
		form_id: form,
		store_id: store,
		area_id: area,
		year,
		type
	});
};

const fetchUserReport = async (
	year: number,
	form: number,
	store: number,
	area?: number
) => {
	const response = await cmsClient.get<IUserReport[]>(
		"/observations/reports/user-by-month/",
		{ params: { year, form, store, area } }
	);

	return response.data;
};

const useUserReport = (
	year: number,
	form: number,
	storeId: number,
	areaId?: number,
	compliancy?: boolean,
	observationType?: IObservationType
) => {
	const query = useQuery(
		["user-report", { year, form, storeId, areaId }],
		() => fetchUserReport(year, form, storeId, areaId ?? undefined),
		{
			select: (list) => adaptUserReport(list)
		}
	);

	const months = Array.from({ length: 12 }, (_, i) => i).map((month) =>
		dayjs().month(month).format("MMM")
	);

	const monthlyColumns = months.map((month) => {
		return columnHelper.accessor(month, {
			cell: ({ cell, row }) => {
				if (observationType === "GREAT_CONVERSATION") {
					return (
						<GreatConversationTableCell
							cellValue={cell.getValue()}
							userId={row.original.user_id}
							storeId={storeId}
						/>
					);
				}

				return (
					<IndividualObservationTableCell
						cellValue={cell.getValue()}
						userId={row.original.user_id}
					/>
				);
			},
			header: month
		});
	});

	const columns = [
		columnHelper.display({
			id: "user-name",
			cell: ({ row }) => row.original.name,
			header: "User Name"
		}),
		...monthlyColumns
	];

	const download = async () => {
		try {
			await requestUserReportExport(
				year,
				form,
				compliancy ? "compliant" : "complete",
				storeId,
				areaId
			);
			toast.success(
				<CustomToast
					type="Success"
					message="A link to your export (User by month) has been generated. Check your mail inbox for the link."
				/>,
				{ hideProgressBar: true }
			);
		} catch (error) {
			toast.error(
				<CustomToast type="Error" message="Something went wrong, try again" />,
				{ hideProgressBar: true }
			);
		}
	};

	return { columns, download, ...query };
};

export default useUserReport;
